import React, { useEffect, useState } from 'react';
import { useMetricsContext } from '../../../contexts/Metrics-Context';
import { useAuthContext } from "../../../contexts/Auth-Context";
import { useChartContext } from '../../../contexts/Chart-Context';
import { TbInfoOctagonFilled } from "react-icons/tb";
import './Metrics-Panel.scss';
import { useSymbolContext } from '../../../contexts/Symbol-Context';
import { useAccountManagerContext } from '../../../contexts/Account-Manager-Context';
import { API_ENDPOINT_USER_DETAILS } from '../../../data/Endpoints-API';
import APIMiddleware from '../../../data/api/Api-Middleware';
import { useTranslation } from 'react-i18next';

const MetricsPanel = ({ mobileMode = false }) => {
    const { t } = useTranslation();
    const { metrics, userCurrencyExchangeRate } = useMetricsContext();
    const { openPositions } = useAccountManagerContext();
    const { user } = useAuthContext();
    const { selectedStyle } = useChartContext();
    const { symbolInfo ,fetchUserDetails2} = useSymbolContext();
    const [stopOut, setStopOut] = useState(0);
    const [totalDeposit, setTotalDeposit] = useState(0); 
    const [totalCommission, setTotalCommission] = useState(0);

    // Fetch user details to get stop_out value
    useEffect(() => {
        const fetchUserDetails = async () => {
            try {
                const response = await APIMiddleware.post(API_ENDPOINT_USER_DETAILS());
                const details = response?.data?.[0];
                if (details) {
                    // Treat 0 and null/undefined safely: default to 0 instead of NaN
                    const rawStopOut = details.stop_out;
                    setStopOut(
                        rawStopOut === null || rawStopOut === undefined || rawStopOut === ''
                            ? 0
                            : Number(rawStopOut)
                    );
                    setTotalDeposit(Number(details.totalDeposit ?? 0));
                    setTotalCommission(Number(details.totalCommission ?? 0));
                }
            } catch (error) {
                console.error("Error fetching user details:", error);
            }
        };
        fetchUserDetails();
    }, [fetchUserDetails2]);

    const isValidNumber = (value) => typeof value === 'number' && !isNaN(value);

    // Calculate all metric values
    const hasOpenPositions = openPositions && openPositions.length > 0;
    // Only show currency after WS metrics include the real symbol (no static €)
    const currencySymbol = metrics?.userCurrencySymbol || '';
    const currencyReady = Boolean(currencySymbol);
    const withCurrency = (amount) => {
        if (!currencyReady) return '---';
        return `${currencySymbol} ${amount}`;
    };
    const balance = withCurrency(isValidNumber(metrics?.balance) ? ((metrics.balance * userCurrencyExchangeRate).toFixed(2)) : 'N/A');
    const equity = withCurrency(isValidNumber(metrics?.equity) ? `${(metrics.equity * userCurrencyExchangeRate).toFixed(2)}` : 'N/A');
    const margin = !currencyReady || !hasOpenPositions
        ? '---'
        : withCurrency(isValidNumber(metrics?.totalMargin) ? `${(metrics.totalMargin * userCurrencyExchangeRate).toFixed(2)}` : 'N/A');
    const freeMargin = withCurrency(isValidNumber(metrics?.freeMargin) ? `${(metrics.freeMargin * userCurrencyExchangeRate).toFixed(2)}` : 'N/A');
    const bonus = withCurrency(isValidNumber(metrics?.totalBonus) ? `${(metrics.totalBonus * userCurrencyExchangeRate).toFixed(2)}` : 'N/A');
    const marginLevel = !currencyReady || !hasOpenPositions
        ? '---'
        : `${isValidNumber(metrics?.marginLevel) ? `${metrics.marginLevel.toFixed(2)} %` : 'N/A'}`;
    const netPnlNumber = !currencyReady || !hasOpenPositions
        ? '---'
        : (isValidNumber(metrics?.totalUnrealizedPnL) ? `${(metrics.totalUnrealizedPnL * userCurrencyExchangeRate).toFixed(2)}` : 'N/A');
    const rlPnlNumber = !currencyReady
        ? '---'
        : (isValidNumber(metrics?.todayRealizedPnL) ? `${(metrics.todayRealizedPnL * userCurrencyExchangeRate).toFixed(2)}` : '---');
    const netPnl = (netPnlNumber === '---' || netPnlNumber === 'N/A') ? netPnlNumber : withCurrency(netPnlNumber);
    const rlPnl = (rlPnlNumber === '---' || rlPnlNumber === 'N/A') ? rlPnlNumber : withCurrency(rlPnlNumber);
    const stopOutNumber = Number(stopOut);
    const stopOutValue = Number.isFinite(stopOutNumber) ? `${stopOutNumber.toFixed(2)} %` : '0.00 %';

    const totalDepositValue = withCurrency(Number(totalDeposit ?? 0).toFixed(2));
    const totalCommissionValue = withCurrency(Number(totalCommission ?? 0).toFixed(2));
    const formatSignedCurrencyValue = (value) => {
        if (!currencyReady || value === '---' || value === 'N/A') return value === '---' || value === 'N/A' ? value : '---';
        const num = Number(value);
        if (!Number.isFinite(num)) return value;
        const absolute = Math.abs(num).toFixed(2);
        const symbolEl = <span className="currency-symbol">{currencySymbol}</span>;
        return num < 0
            ? (
                <>
                    - {symbolEl} {absolute}
                </>
            )
            : (
                <>
                    {symbolEl} {absolute}
                </>
            );
    };

    if (mobileMode) {
        // Mobile view implementation (2-column layout)
        return (
            <div className="metrics-panel mobile">
                <ul className="metrics-list">
                    <span className='mobile-view-style'>
                        <li className='metricsData'>
                            <strong className='multiline-truncate' title={t('metrics.tipBalance')}>
                                <i><TbInfoOctagonFilled /></i> {t('metrics.profit')}
                            </strong>
                            <span className='multiline-truncate'
                            style={{
                                color: parseFloat(metrics?.todayRealizedPnL) >= 0
                                ? selectedStyle.buyColor
                                : selectedStyle.sellColor
                            }} 
                            title={rlPnl}
                            >
                                {(rlPnlNumber === '---' || rlPnlNumber === 'N/A')
                                    ? rlPnlNumber
                                    : formatSignedCurrencyValue(rlPnlNumber)
                                }
                            </span>
                        </li>
                        <li className='metricsData'>
                            <strong className='multiline-truncate' title={t('metrics.tipEquity')}>
                                <i><TbInfoOctagonFilled /></i> {t('metrics.deposit')}
                            </strong>
                            <span className='multiline-truncate' title={totalDepositValue}>{totalDepositValue}</span>
                        </li>
                    </span>

                    <span className='mobile-view-style'>
                        <li className='metricsData'>
                            <strong className='multiline-truncate' title={t('metrics.tipMargin')}>
                                <i><TbInfoOctagonFilled /></i> {t('metrics.commission')}
                            </strong>
                            <span className='multiline-truncate' title={totalCommissionValue}>{totalCommissionValue}</span>
                        </li>
                        <li className='metricsData'>
                            <strong className='multiline-truncate' title={t('metrics.tipFreeMargin')}>
                                <i><TbInfoOctagonFilled /></i> {t('metrics.balance')}
                            </strong>
                            <span className='multiline-truncate' title={balance}>{balance}</span>
                        </li>
                    </span>

                    <span className='mobile-view-style'>
                        <li className='metricsData'>
                            <strong className='multiline-truncate' title={t('metrics.tipMarginLevel')}>
                                <i><TbInfoOctagonFilled /></i> {t('metrics.margin')}
                            </strong>
                            <span className='multiline-truncate' title={margin}>{margin}</span>
                        </li>
                        <li className='metricsData'>
                            <strong className='multiline-truncate' title={t('metrics.tipTodayRlPnl')}>
                                <i><TbInfoOctagonFilled /></i> {t('metrics.freeMargin')}
                            </strong>
                            <span className='multiline-truncate' title={freeMargin}>
                                {freeMargin}
                            </span>
                        </li>
                    </span>

                    <span className='mobile-view-style'>
                        <li className='metricsData'>
                            <strong className='multiline-truncate' title={t('metrics.tipUnRNetPnl')}>
                                <i><TbInfoOctagonFilled /></i> {t('metrics.marginLevel')}
                            </strong>
                            <span className='multiline-truncate' title={marginLevel}>
                                {marginLevel}
                            </span>
                        </li>
                    </span>

                    {/* {symbolInfo?.show_stop_out_details && (
                        <span className='mobile-view-style bottom-view'>
                            <li className='metricsData stop_out'>
                                <strong className='multiline-truncate' title='Stop Out Level'>
                                    <i><TbInfoOctagonFilled /></i> {t('metrics.stopOut')}
                                </strong>
                                <span>{stopOutValue}</span>
                            </li>
                        </span>
                    )} */}

                    <span className='mobile-view-style'>
                        <li className='metricsData'>
                            <strong className='multiline-truncate' title={t('metrics.tipUnRNetPnl')}>
                                <i><TbInfoOctagonFilled /></i> {t('metrics.unRNetPnl')}
                            </strong>
                            <span
                                style={{
                                    color: parseFloat(metrics?.totalUnrealizedPnL) >= 0
                                        ? selectedStyle.buyColor
                                        : selectedStyle.sellColor
                                }}
                                className='multiline-truncate'
                                title={netPnl}
                            >
                                {(netPnlNumber === '---' || netPnlNumber === 'N/A')
                                    ? netPnlNumber
                                    : formatSignedCurrencyValue(netPnlNumber)
                                }
                            </span>
                        </li>
                    </span>
                </ul>
            </div>
        );
    }

    // Desktop view implementation (horizontal scrolling)
    return (
        <div className="metrics-panel global-platfrom-dd">
            <div className="metrics-scroll-container">
                <ul className="metrics-list">
                    <li className='metric-item'>
                        <div className="metric-content">
                            <strong className='metric-title' title={t('metrics.tipBalance')}>
                                <i><TbInfoOctagonFilled /></i> {t('metrics.balance')}
                            </strong>
                            <span className='metric-value' title={balance}>{balance}</span>
                        </div>
                    </li>
                    <li className='metric-item'>
                        <div className="metric-content">
                            <strong className='metric-title' title={t('metrics.tipEquity')}>
                                <i><TbInfoOctagonFilled /></i> {t('metrics.equity')}
                            </strong>
                            <span className='metric-value' title={equity}>{equity}</span>
                        </div>
                    </li>
                    <li className='metric-item'>
                        <div className="metric-content">
                            <strong className='metric-title' title={t('metrics.tipMargin')}>
                                <i><TbInfoOctagonFilled /></i> {t('metrics.margin')}
                            </strong>
                            <span className='metric-value' title={margin}>{margin}</span>
                        </div>
                    </li>
                    <li className='metric-item'>
                        <div className="metric-content">
                            <strong className='metric-title' title={t('metrics.tipFreeMargin')}>
                                <i><TbInfoOctagonFilled /></i> {t('metrics.freeMargin')}
                            </strong>
                            <span className='metric-value' title={freeMargin}>{freeMargin}</span>
                        </div>
                    </li>
                    <li className='metric-item'>
                        <div className="metric-content">
                            <strong className='metric-title' title={t('metrics.tipBonus')}>
                                <i><TbInfoOctagonFilled /></i> {t('metrics.bonus')}
                            </strong>
                            <span className='metric-value' title={bonus}>{bonus}</span>
                        </div>
                    </li>
                    <li className='metric-item'>
                        <div className="metric-content">
                            <strong className='metric-title' title={t('metrics.tipMarginLevel')}>
                                <i><TbInfoOctagonFilled /></i> {t('metrics.marginLevel')}
                            </strong>
                            <span className='metric-value' title={marginLevel}>{marginLevel}</span>
                        </div>
                    </li>
                    <li className='metric-item'>
                        <div className="metric-content">
                            <strong className='metric-title' title={t('metrics.tipTodayRlPnl')}>
                                <i><TbInfoOctagonFilled /></i> {t('metrics.todayRlPnl')}
                            </strong>
                            <span 
                                className='metric-value'
                                title={rlPnl}
                                style={{
                                    color: parseFloat(metrics?.todayRealizedPnL) >= 0
                                        ? selectedStyle.buyColor
                                        : selectedStyle.sellColor
                                }}
                            >
                                {(rlPnlNumber === '---' || rlPnlNumber === 'N/A')
                                    ? rlPnlNumber
                                    : formatSignedCurrencyValue(rlPnlNumber)
                                }
                            </span>
                        </div>
                    </li>
                    <li className='metric-item'>
                        <div className="metric-content">
                            <strong className='metric-title' title={t('metrics.tipUnRNetPnl')}>
                                <i><TbInfoOctagonFilled /></i> {t('metrics.unRNetPnl')}
                            </strong>
                            <span 
                                className='metric-value'
                                title={netPnl}
                                style={{
                                    color: parseFloat(metrics?.totalUnrealizedPnL) >= 0
                                        ? selectedStyle.buyColor
                                        : selectedStyle.sellColor
                                }}
                            >
                                {(netPnlNumber === '---' || netPnlNumber === 'N/A')
                                    ? netPnlNumber
                                    : formatSignedCurrencyValue(netPnlNumber)
                                }
                            </span>
                        </div>
                    </li>
                    {symbolInfo?.show_stop_out_details ? (
                        <li className='metric-item stop_out'>
                            <div className="metric-content">
                                <strong className='metric-title' title='Stop Out Level'>
                                    <i><TbInfoOctagonFilled /></i> {t('metrics.stopOut')}
                                </strong>
                                <span className='metric-value'>{stopOutValue}</span>
                            </div>
                        </li>
                    ) : null }
                </ul>
            </div>
        </div>
    );
};

export default MetricsPanel;